import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, OceanTag } from "./kit";
import { OCEAN_EXPERTISE, OCEAN_PREFERRED_REGIONS, OIMG } from "../../data/oceanContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", location: "", expertise: "", certification: "", areas_of_interest: "", preferred_region: "", availability: "", motivation: "" };

const inputCls =
  "w-full bg-onavy/70 border border-oreef/20 text-oseaglass placeholder:text-oslate/70 px-4 py-3 text-sm focus:outline-none focus:border-oreef transition-colors duration-300";

export default function JoinExpedition() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Name is required";
    if (!EMAIL_RE.test(form.email)) errs.email = "A valid email is required";
    if (form.location.trim().length < 2) errs.location = "Location is required";
    if (!form.expertise) errs.expertise = "Select your relevant expertise";
    if (form.areas_of_interest.trim().length < 2) errs.areas_of_interest = "Areas of interest are required";
    if (!form.preferred_region) errs.preferred_region = "Select a preferred region";
    if (form.motivation.trim().length < 10) errs.motivation = "Motivation must be at least 10 characters";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/ocean/enquiries/interest`, form);
      toast.success("Expression of interest received. Thank you for your interest in the mission.");
      setForm(EMPTY);
      setErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="join" data-testid="ocean-join-section" className="relative py-24 lg:py-32 bg-oabyss/30 overflow-hidden">
      <div className="absolute inset-0 opacity-25">
        <img src={OIMG.diver} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-onavy/85 via-onavy/80 to-onavy" />
      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <OceanTag index="08" label="Join the Expedition" />
              <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-oseaglass leading-[0.95]">
                Your next dive could ask a <span className="text-oreef">bigger question</span>
              </h2>
              <p className="mt-6 text-base text-oseaglass/70 leading-relaxed">
                We are exploring the expertise this initiative may seek — from certified divers and marine biologists to
                remote-sensing specialists, GIS engineers and underwater photographers.
              </p>
              <div className="mt-8 border border-ocoral/25 bg-ocoral/5 p-6" data-testid="join-disclaimer">
                <p className="text-sm text-oseaglass/75 leading-relaxed">
                  This is an expression of interest only. No guaranteed dive opportunities, funded travel, paid positions
                  or confirmed vacancies are advertised. Diving certification is optional for non-diving technical or
                  research roles.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form onSubmit={submit} noValidate className="border border-oreef/20 bg-oabyss/60 backdrop-blur-md p-7 sm:p-9" data-testid="ocean-interest-form">
              <div className="font-mono text-[10px] tracking-[0.3em] text-oreef mb-7">EXPRESSION OF INTEREST — SECURE SUBMISSION</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="oif-name" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">NAME *</label>
                  <input id="oif-name" data-testid="ocean-interest-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
                  {errors.name && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-name">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="oif-email" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">PROFESSIONAL EMAIL *</label>
                  <input id="oif-email" type="email" data-testid="ocean-interest-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@email.com" />
                  {errors.email && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-email">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                <div>
                  <label htmlFor="oif-location" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">LOCATION *</label>
                  <input id="oif-location" data-testid="ocean-interest-input-location" className={inputCls} value={form.location} onChange={set("location")} placeholder="City, Country" />
                  {errors.location && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-location">{errors.location}</p>}
                </div>
                <div>
                  <label htmlFor="oif-expertise" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">RELEVANT EXPERTISE *</label>
                  <select id="oif-expertise" data-testid="ocean-interest-input-expertise" className={`${inputCls} appearance-none`} value={form.expertise} onChange={set("expertise")}>
                    <option value="" disabled>Select expertise</option>
                    {OCEAN_EXPERTISE.map((x) => <option key={x} value={x} className="bg-onavy">{x}</option>)}
                  </select>
                  {errors.expertise && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-expertise">{errors.expertise}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="oif-cert" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">DIVING CERTIFICATION &amp; EXPERIENCE <span className="text-oslate/60">(optional)</span></label>
                <input id="oif-cert" data-testid="ocean-interest-input-certification" className={inputCls} value={form.certification} onChange={set("certification")} placeholder="e.g. PADI Advanced Open Water, 80 logged dives" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                <div>
                  <label htmlFor="oif-region" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">PREFERRED COASTAL REGION *</label>
                  <select id="oif-region" data-testid="ocean-interest-input-region" className={`${inputCls} appearance-none`} value={form.preferred_region} onChange={set("preferred_region")}>
                    <option value="" disabled>Select a region</option>
                    {OCEAN_PREFERRED_REGIONS.map((r) => <option key={r} value={r} className="bg-onavy">{r}</option>)}
                  </select>
                  {errors.preferred_region && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-region">{errors.preferred_region}</p>}
                </div>
                <div>
                  <label htmlFor="oif-avail" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">AVAILABILITY <span className="text-oslate/60">(optional)</span></label>
                  <input id="oif-avail" data-testid="ocean-interest-input-availability" className={inputCls} value={form.availability} onChange={set("availability")} placeholder="e.g. weekends, seasonal, flexible" />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="oif-areas" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">AREAS OF SCIENTIFIC / TECHNICAL INTEREST *</label>
                <input id="oif-areas" data-testid="ocean-interest-input-areas" className={inputCls} value={form.areas_of_interest} onChange={set("areas_of_interest")} placeholder="e.g. reef mapping, ocean-colour analysis, underwater video" />
                {errors.areas_of_interest && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-areas">{errors.areas_of_interest}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="oif-motivation" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">SHORT MOTIVATION STATEMENT *</label>
                <textarea id="oif-motivation" rows={4} data-testid="ocean-interest-input-motivation" className={`${inputCls} resize-y`} value={form.motivation} onChange={set("motivation")} placeholder="Why would you like to be part of the mission?" />
                {errors.motivation && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-interest-error-motivation">{errors.motivation}</p>}
              </div>

              <button type="submit" disabled={sending} data-testid="ocean-interest-submit-button"
                className="mt-8 w-full bg-oreef text-onavy font-mono text-xs font-semibold uppercase tracking-[0.25em] py-4 clip-corner hover:bg-oseaglass transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                {sending ? "Transmitting…" : "Express Interest"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
